import type { AuthSession } from "@/modules/auth/public";
import { createLogger } from "@/logging";

/** 세션이 끝난 이유. 브라우저 종료만 재개용 로컬 상태를 보존한다. */
export type AppSessionEndReason =
  | "logout"
  | "invalidated"
  | "remoteLogout"
  | "browserExit"
  | "appUnmount"
  | "sessionReplaced";

export type AppSessionResource = {
  name: string;
  start(session: AuthSession): void;
  dispose(reason: AppSessionEndReason): void;
};

const logger = createLogger("app/sessionLifecycle");

/**
 * 로그인 세션에 연결된 앱 전역 리소스를 시작하고, 역순으로 정리한다.
 *
 * Owns:
 * - 같은 사용자 세션의 중복 시작 방지.
 * - 사용자 교체 시 기존 세션 정리 후 새 세션 시작.
 * - 예외 격리. 일부 리소스 정리가 실패해도 나머지 리소스를 끝까지 정리한다.
 *
 * Does not own:
 * - 인증 토큰·React 인증 상태.
 * - 각 리소스 내부의 타이머, 네트워크, 저장소 정리 방식.
 */
export class AppSessionLifecycle {
  private activeUserId: string | null = null;

  constructor(private readonly resources: readonly AppSessionResource[]) {}

  start(session: AuthSession): void {
    if (this.activeUserId === session.uid) {
      return;
    }
    if (this.activeUserId !== null) {
      this.dispose("sessionReplaced");
    }

    const started: AppSessionResource[] = [];
    try {
      for (const resource of this.resources) {
        started.push(resource);
        resource.start(session);
      }
      this.activeUserId = session.uid;
    } catch (error) {
      this.disposeStartedResources(started, "sessionReplaced");
      throw error;
    }
  }

  dispose(reason: AppSessionEndReason): void {
    if (this.activeUserId === null) {
      return;
    }
    this.activeUserId = null;
    this.disposeStartedResources([...this.resources], reason);
  }

  private disposeStartedResources(
    resources: AppSessionResource[],
    reason: AppSessionEndReason,
  ): void {
    for (const resource of resources.reverse()) {
      try {
        resource.dispose(reason);
      } catch (error) {
        logger.error(`Session resource dispose failed: ${resource.name}.`, error);
      }
    }
  }
}
